export const MAX_NAME_LENGTH = 40;
export const MAX_TITLE_LENGTH = 80;
export const MAX_CHAT_LENGTH = 1000;
/**
 * The name of an account made without one: Apple shares a name only the first
 * time someone signs in, and an address Apple made up (Hide My Email) says
 * nothing about it. The app then asks for the name people will see.
 */
export const PLACEHOLDER_NAME = 'New user';

// Control characters, plus the invisible formatting characters (zero-width,
// bidi overrides) that let one name impersonate another.
const UNSAFE_CHARS = /[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁠-⁩﻿]/g;

function clean(input: string, max: number): string {
  // Whitespace first: a line break or tab separates words and must not glue them together.
  const collapsed = input.normalize('NFC').replace(/\s+/g, ' ').replace(UNSAFE_CHARS, '').replace(/ +/g, ' ').trim();
  // Cut on code points, not UTF-16 units, so an emoji is never split in half.
  return Array.from(collapsed).slice(0, max).join('').trim();
}

/** Returns a safe display name, or null when nothing usable is left. */
export function sanitizeDisplayName(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const name = clean(input, MAX_NAME_LENGTH);
  return name.length > 0 ? name : null;
}

export function sanitizeTitle(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const title = clean(input, MAX_TITLE_LENGTH);
  return title.length > 0 ? title : null;
}

/** Chat keeps line breaks; everything else is treated like a name. */
export function sanitizeChatText(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const text = input
    .normalize('NFC')
    .replace(/\r\n?/g, '\n')
    .replace(/[\u0000-\u0009\u000b-\u001f\u007f-\u009f‪-‮⁦-⁩]/g, '')
    .trim();
  if (text.length === 0) return null;
  if (Array.from(text).length > MAX_CHAT_LENGTH) return null;
  return text;
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = Array.from(words[0]!)[0] ?? '?';
  if (words.length === 1) return first.toUpperCase();
  const last = Array.from(words[words.length - 1]!)[0] ?? '';
  return (first + last).toUpperCase();
}

const AVATAR_COLORS = ['#4A6078', '#5C4A6E', '#3F6152', '#6E5440', '#3E5866', '#6B4A52', '#5E4A38', '#4A5E3E'];
const TILE_COLORS = ['#2A3A4A', '#3A2F45', '#2F4038', '#45362A', '#2B3D47', '#3D2E33', '#3A2F26', '#2F3A2A'];

function hash(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Stable colours per person so a tile does not change colour between renders. */
export function avatarColors(key: string): { avatar: string; tile: string } {
  const i = hash(key) % AVATAR_COLORS.length;
  return { avatar: AVATAR_COLORS[i]!, tile: TILE_COLORS[i]! };
}

export type LinkPart = { type: 'text'; text: string } | { type: 'link'; text: string; href: string };

/**
 * Splits chat text into plain text and http(s) links. The caller renders the
 * parts as text nodes and anchors, so no HTML from a message is ever parsed.
 */
export function linkify(text: string): LinkPart[] {
  const parts: LinkPart[] = [];
  const pattern = /https?:\/\/[^\s<>"']+/gi;
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    let url = match[0];
    const start = match.index;
    // Trailing punctuation belongs to the sentence, not the link.
    const trimmed = url.replace(/[.,!?;:)\]]+$/, '');
    url = trimmed.length > 0 ? trimmed : url;
    if (start > last) parts.push({ type: 'text', text: text.slice(last, start) });
    parts.push({ type: 'link', text: url, href: url });
    last = start + url.length;
  }
  if (last < text.length) parts.push({ type: 'text', text: text.slice(last) });
  return parts;
}
